// components/ScraperDashboard/ScrapeHistoryCard.js
// Individual scrape run card with expandable inline results
import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, AlertCircle, Clock, ChevronDown, ChevronUp,
  Database, FileText, DollarSign, Building, Tag, Calendar,
  Download, ExternalLink, Eye
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import useDepartments from '../../hooks/useDepartments';
import PoLink from '../PoDetailModal/PoLink';
import './ScrapeHistoryCard.css';

/**
 * Extract the best available date from a supplier record.
 * Checks multiple possible date fields in priority order.
 */
function extractSupplierDate(supplier) {
  // Direct date column first
  if (supplier.date) return supplier.date;

  // Check metadata for various date field names
  const m = supplier.metadata || {};

  // Priority order of date fields to check
  const dateFields = [
    m.date,
    m.fromDate,
    m.startDate,
    m.toDate,
    m.endDate,
    m._raw?.obj?.['Start Date'],
    m._raw?.obj?.['End Date'],
    m._raw?.obj?.['Date'],
  ];

  for (const val of dateFields) {
    if (val) return val;
  }

  return null;
}

/**
 * ScrapeHistoryCard - Displays a single scrape run with expandable results
 *
 * @param {Object} props
 * @param {Object} props.run - Scrape run data from API
 * @param {boolean} props.defaultExpanded - Whether to expand by default
 * @param {Function} props.onRefresh - Callback to refresh data
 * @param {Function} props.onSupplierClick - Callback when a supplier row is clicked for drill-down
 */
function ScrapeHistoryCard({ run, defaultExpanded = false, onRefresh, onSupplierClick }) {
  const { deptMap } = useDepartments();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [suppliers, setSuppliers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Status: 'completed' (new), 'success' (legacy), 'partial', 'failed', 'running'
  const isSuccess = run.status === 'completed' || run.status === 'success';
  const isPartial = run.status === 'partial';
  const isRunning = run.status === 'running';
  const deptCode = run.criteria?.departmentCode;
  const deptName = deptMap[deptCode] || deptCode || 'All Depts';
  const acqType = run.criteria?.acquisitionType || 'All Types';
  const dateRange = run.criteria?.dateFrom
    ? `${run.criteria.dateFrom} → ${run.criteria.dateTo}`
    : 'No date range';

  // Fetch suppliers when expanded
  const fetchSuppliers = useCallback(async () => {
    if (!run.id || suppliers) return;

    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.get(`/api/scraper/results/${run.id}/suppliers`);
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[ScrapeHistoryCard] Failed to fetch suppliers:', err);
      setError(err.message || 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [run.id, suppliers]);

  useEffect(() => {
    if (expanded && !suppliers && !loading) {
      fetchSuppliers();
    }
  }, [expanded, suppliers, loading, fetchSuppliers]);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatCurrency = (value) => {
    if (!value) return '--';
    return parseFloat(value).toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const saveRate = run.total_suppliers > 0
    ? Math.round((parseInt(run.saved_to_db) / run.total_suppliers) * 100)
    : 0;

  return (
    <div className={`shc-card ${isRunning ? 'shc-running' : isPartial ? 'shc-partial' : isSuccess ? 'shc-success' : 'shc-error'} ${expanded ? 'shc-expanded' : ''}`}>
      {/* Card Header */}
      <div className="shc-header" onClick={toggleExpanded}>
        <div className="shc-status">
          {isRunning ? (
            <Clock size={16} className="shc-icon-running" />
          ) : isPartial ? (
            <AlertCircle size={16} className="shc-icon-partial" />
          ) : isSuccess ? (
            <CheckCircle size={16} className="shc-icon-success" />
          ) : (
            <AlertCircle size={16} className="shc-icon-error" />
          )}
        </div>

        <div className="shc-main">
          <div className="shc-top-row">
            <span className="shc-time">{formatDate(run.started_at)}</span>
            <span className="shc-dept">{deptName}</span>
            <span className="shc-acq">{acqType}</span>
          </div>
          <div className="shc-bottom-row">
            <span className="shc-count">
              <Database size={12} />
              <strong>{run.total_suppliers || 0}</strong> POs
            </span>
            {(isPartial || run.failed_saves > 0) && (
              <span className="shc-quality-badge shc-quality-partial" title={`${run.saved_to_db} of ${run.total_suppliers} saved to DB`}>
                ⚠ {Math.round((run.data_quality_score || 0) * 100)}% saved
              </span>
            )}
            {isRunning && (
              <span className="shc-quality-badge shc-quality-running">● running</span>
            )}
            {run.total_value && (
              <span className="shc-value">
                <DollarSign size={12} />
                {formatCurrency(run.total_value)}
              </span>
            )}
            <span className="shc-duration">
              <Clock size={12} />
              {formatDuration(run.duration_seconds)}
            </span>
            {saveRate < 100 && saveRate > 0 && (
              <span className="shc-save-rate">
                {saveRate}% saved
              </span>
            )}
          </div>
        </div>

        <div className="shc-expand">
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="shc-body">
          {/* Run Details */}
          <div className="shc-details">
            <div className="shc-detail-row">
              <span className="shc-detail-label"><Calendar size={12} /> Date Range</span>
              <span className="shc-detail-value">{dateRange}</span>
            </div>
            <div className="shc-detail-row">
              <span className="shc-detail-label"><FileText size={12} /> Pages</span>
              <span className="shc-detail-value">{run.pages_processed || '--'}</span>
            </div>
            <div className="shc-detail-row">
              <span className="shc-detail-label"><Database size={12} /> Saved</span>
              <span className="shc-detail-value">{run.saved_to_db || 0} / {run.total_suppliers || 0}</span>
            </div>
            {run.named_suppliers && (
              <div className="shc-detail-row">
                <span className="shc-detail-label"><Building size={12} /> Named</span>
                <span className="shc-detail-value">{run.named_suppliers}</span>
              </div>
            )}
          </div>

          {/* Suppliers Table */}
          <div className="shc-suppliers">
            {loading && (
              <div className="shc-loading">Loading PO records...</div>
            )}
            {error && (
              <div className="shc-error-msg">{error}</div>
            )}
            {suppliers && suppliers.length === 0 && (
              <div className="shc-empty">No PO data available</div>
            )}
            {suppliers && suppliers.length > 0 && (
              <>
                <div className="shc-suppliers-header">
                  <span>{suppliers.length} POs</span>
                  {onSupplierClick && (
                    <span className="shc-hint">Click a row to view details</span>
                  )}
                </div>
                <div className="shc-table-wrap">
                  <table className="shc-table">
                    <thead>
                      <tr>
                        <th>Supplier</th>
                        <th>Amount</th>
                        <th>Type</th>
                        <th>Department</th>
                        <th>Date</th>
                        <th>Detail</th>
                        {onSupplierClick && <th></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {suppliers.slice(0, 20).map((s, i) => {
                        const supplierDate = extractSupplierDate(s);
                        return (
                          <tr
                            key={s.document_number || s.id || i}
                            className={onSupplierClick ? 'shc-clickable' : ''}
                            onClick={() => onSupplierClick && onSupplierClick(s)}
                          >
                            <td data-label="Supplier">
                              <div className="shc-supplier-name">
                                {s.supplier_name || 'Unknown'}
                              </div>
                              {s.document_number && (
                                <div className="shc-doc-num">
                                  <PoLink documentNumber={s.document_number} />
                                </div>
                              )}
                            </td>
                            <td data-label="Amount" className="shc-amount">
                              {s.amount ? formatCurrency(s.amount) : '--'}
                            </td>
                            <td data-label="Type">
                              <span className="shc-type-badge">
                                {s.acquisition_type || '--'}
                              </span>
                            </td>
                            <td data-label="Department">
                              {s.department_name || s.department_code || '--'}
                            </td>
                            <td data-label="Date">
                              {supplierDate ? new Date(supplierDate).toLocaleDateString() : '--'}
                            </td>
                            <td data-label="Detail">
                              <span className={`shc-detail-badge shc-detail-${s.detail_status || 'not_queued'}`}>
                                {s.detail_status === 'complete' && s.line_item_count > 0
                                  ? `${s.line_item_count} lines`
                                  : s.detail_status === 'complete'
                                  ? 'Done'
                                  : s.detail_status === 'queued' || s.detail_status === 'processing'
                                  ? 'Pending'
                                  : s.detail_status === 'failed'
                                  ? 'Failed'
                                  : '--'}
                              </span>
                            </td>
                            {onSupplierClick && (
                              <td className="shc-view-cell">
                                <Eye size={14} className="shc-view-icon" />
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {suppliers.length > 20 && (
                  <div className="shc-more">
                    Showing 20 of {suppliers.length} POs
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ScrapeHistoryCard;
